// App-wide "you have unsaved changes" guard.
//
// Several surfaces stage work in memory and persist it only on an explicit
// Commit/Save (the sources working set, the library's staged approvals and
// deletes, the article gallery, the feeds curation, an open rich-text editor, a
// dialog draft). Each of them registers a dirty-check here; a single
// <UnsavedChangesHost /> watches navigation and asks before the work is thrown
// away. Same shape as `confirmStore` / `toastStore`: a class using runes,
// exported as a module singleton, with one host mounted in the root layout.
//
// THE SAME-PATHNAME RULE is the non-obvious part. Several "tab" strips in this
// app are real navigations that only change a search param — the sources sheet
// tabs (`?feed_type=`), the Feeds subcategory pills (`?feed=`), the narratives
// editor (`?narrative=`). None of them unmount the surface holding the staged
// work, so they are not a departure and must not prompt. Only a change of
// pathname (or a page unload) is. Dirtiness is not forgotten by letting one
// through: the guard stays registered, so the next cross-path navigation still
// asks.

import { confirmStore } from '$lib/confirm/confirm.svelte';

export type UnsavedGuard = {
  // Whether this surface currently holds unsaved work. Called at navigation
  // time, outside any reactive context, so it must be a closure over the
  // surface's own state rather than a snapshot of it.
  isDirty: () => boolean;
  // Copy for the confirmation, when the default wording is too vague.
  title?: string;
  message?: string;
  // Run after the user chooses to leave — a chance to release resources the
  // staged work holds (blob URLs and the like).
  onDiscard?: () => void;
  // Run after the user chooses to STAY. The prompt names work the user may not
  // be looking at — it can have been staged on a tab they have since navigated
  // away from — so this is the surface's chance to put it back on screen.
  onKeepEditing?: () => void;
  // The path prefix within which the surface holding this work stays mounted,
  // so navigating inside it costs nothing. The agent chat is the reason this
  // exists: it is mounted by the agent-detail LAYOUT, once for every tab, so a
  // tab switch (`/agents/{id}/feeds` → `/agents/{id}/sources` — a different
  // pathname) keeps the composer draft, and prompting there is pure noise.
  // Omit it for a surface that unmounts on any pathname change, which is the
  // common case.
  scope?: () => string;
};

// The default wording, shared by the navigation guard and by the dialogs'
// `confirmClose` hooks so a discard reads the same however it was triggered.
export const DISCARD_TITLE = 'Discard unsaved changes?';
export const DISCARD_MESSAGE = 'You have unsaved changes. Leaving now will discard them.';
export const DISCARD_CONFIRM = 'Discard';
export const DISCARD_CANCEL = 'Keep editing';

// Ask whether to throw unsaved work away. Exported on its own so a dialog's
// `confirmClose` can reuse the exact copy without going through the registry.
export const confirmDiscard = (message = DISCARD_MESSAGE, title = DISCARD_TITLE) =>
  confirmStore.confirm({
    title,
    message,
    confirmLabel: DISCARD_CONFIRM,
    cancelLabel: DISCARD_CANCEL,
    danger: true
  });

class UnsavedChanges {
  // Insertion-ordered, which is what lets `confirmLeave` describe the most
  // recently registered surface at risk — a dialog opened over a dirty page is
  // the more specific answer than the page under it.
  #guards: UnsavedGuard[] = [];

  // Register for as long as the surface lives. Returns the release fn; prefer
  // the `guardUnsaved` helper below, which wires that to the component.
  register(guard: UnsavedGuard): () => void {
    this.#guards.push(guard);
    return () => {
      const i = this.#guards.indexOf(guard);
      if (i !== -1) this.#guards.splice(i, 1);
    };
  }

  // A guard can throw only if a surface's own state read throws mid-teardown;
  // treat that as clean rather than trapping the user on the page.
  #dirtyOf(guard: UnsavedGuard): boolean {
    try {
      return guard.isDirty();
    } catch {
      return false;
    }
  }

  get dirty(): boolean {
    return this.#guards.some((g) => this.#dirtyOf(g));
  }

  // Set by a caller that has already asked and been told to go ahead, so the
  // navigation it is about to make isn't questioned a second time. Consumed by
  // the next `blocks()` call — `logout()` confirms, then redirects.
  #allowOnce = false;

  allowOnce() {
    this.#allowOnce = true;
  }

  // Whether `guard`'s surface is still mounted at `to`, so its work is safe.
  // A guard that names no scope is assumed to live on exactly the page it
  // registered from, which is what makes a search-param change (the sources
  // sheet tabs, the Feeds pills, `?narrative=`) free.
  #survives(guard: UnsavedGuard, from: URL, to: URL): boolean {
    if (from.origin !== to.origin) return false;
    const scope = guard.scope?.() ?? from.pathname;
    return to.pathname === scope || to.pathname.startsWith(`${scope}/`);
  }

  // The dirty guards this navigation would destroy. Asked per guard, not once
  // for the page: whether the work is at risk depends on where the surface
  // holding it lives, and an agent-detail tab switch unmounts the tab while
  // leaving the layout's chat composer untouched.
  #atRisk(from: URL | undefined, to: URL | null | undefined): UnsavedGuard[] {
    const dirty = this.#guards.filter((g) => this.#dirtyOf(g));
    // A page unload takes everything with it.
    if (!to || !from) return dirty;
    return dirty.filter((g) => !this.#survives(g, from, to));
  }

  // Whether this navigation has to be stopped. `to` is absent for a page
  // unload (tab close, reload, an external link), where cancelling is what
  // makes the browser show its own prompt.
  blocks(from: URL | undefined, to: URL | null | undefined): boolean {
    if (this.#allowOnce) {
      this.#allowOnce = false;
      return false;
    }
    return this.#atRisk(from, to).length > 0;
  }

  // Ask, and on a yes let every dirty surface release what it was holding.
  // Resolves true when the caller may proceed.
  async confirmLeave(from?: URL, to?: URL | null): Promise<boolean> {
    const risked = this.#atRisk(from, to);
    // The most recently registered one — a dialog opened over a dirty page
    // describes the dialog, the thing the user is actually looking at.
    const guard = risked[risked.length - 1];
    if (!guard) return true;

    const ok = await confirmDiscard(guard.message ?? DISCARD_MESSAGE, guard.title ?? DISCARD_TITLE);
    if (!ok) {
      // Every at-risk surface gets the chance to reveal itself, not just the
      // one that was described — each decides for itself whether it is already
      // in view.
      for (const g of risked) g.onKeepEditing?.();
      return false;
    }

    for (const g of risked) g.onDiscard?.();
    return true;
  }
}

export const unsavedChanges = new UnsavedChanges();

// Register a guard for the lifetime of the calling component. The effect body
// reads nothing reactive, so it runs once and its cleanup runs on destroy.
// `guard` must be a stable object built at component init — its `isDirty`
// closure is what carries the live state.
export function guardUnsaved(guard: UnsavedGuard): void {
  $effect(() => unsavedChanges.register(guard));
}
